import { CHAT_STATUS_CLOSED, USER_TYPES } from '@sv-support-sdk/utils/src/constants/chat';
import {
  MESSAGE_TYPES,
  MESSAGE_USER_TYPES,
  MESSAGE_USER_TYPES_LIST,
} from '@sv-support-sdk/utils/src/constants/chatMessages';
import { emailValidation } from '@sv-support-sdk/utils/src/helpers/validations';
import { getLastMessage } from '@sv-support-sdk/utils/src/helpers/chatMessage';
import { getCurrentTimeCreate } from '@sv-support-sdk/utils/src/helpers/toLocaleTime';
import { IMessage } from '@sv-support-sdk/chat-shared-types/src/common/messages';

import { TThunk } from '../index';
import {
  addMessage,
  clearTempMessages,
  setReplied,
  setUnreadMessages,
  updateCallbackAnswer,
} from '../slices/messages';
import { v4 as uuidV4 } from 'uuid';
import { closeChat, loadChat } from './chat';
import { getCookie, setCookie } from '../../helpers/cookies';
import { CHAT_LAST_MESSAGE, CHAT_LAST_MESSAGE_READ } from '../../constants/storage';
import { addSupport } from '../slices/chat';
import { updateUser } from '../slices/user';
import { $l } from '../../locales';
import { playSound } from '../../helpers/media';

export const sendMessage =
  (message: string, resendUuid?: string): TThunk =>
  async (dispatch, getState, api) => {
    const {
      user,
      chat,
      messages: { replied, messages },
    } = getState();

    const uuid = resendUuid || uuidV4();

    const userType =
      user.type === USER_TYPES.REGISTERED
        ? MESSAGE_USER_TYPES.INTERNAL_USER
        : MESSAGE_USER_TYPES.CHAT_TEMP_USER;

    try {
      if (chat.id) {
        await api.messages.sendMessage({
          user_id: user.id,
          chat_id: chat.id,
          hash: chat.hash,
          user_type: userType,
          message,
          uuid,
        });

        dispatch(
          addMessage({
            user_id: user.id,
            chat_id: chat.id,
            hash: chat.hash,
            createdAt: getCurrentTimeCreate(),
            userType: userType,
            authorType: userType,
            type: MESSAGE_TYPES.MESSAGE,
            uuid,
            error: false,
            tempMessage: true,
            message,
          }),
        );
      } else {
        if (messages.length > 3) {
          dispatch(
            addMessage({
              type: MESSAGE_TYPES.DIVIDER,
              message: $l('NEW_CONVERSATION'),
              id: uuidV4(),
            }),
          );
        }
        await dispatch(loadChat({ init_message: message, messageUuid: uuid }));
      }
    } catch (e) {
      dispatch(
        addMessage({
          uuid,
          user_id: user.id,
          chat_id: chat.id,
          hash: chat.hash,
          userType: userType,
          authorType: userType,
          type: 'message',
          message,
          createdAt: null,
          error: true,
        }),
      );
      return;
    }

    if (!chat.connectedSupports.length && !replied) {
      dispatch(
        addMessage({
          type: MESSAGE_TYPES.MESSAGE,
          message: $l('WILL_REPLY'),
          uuid: uuidV4(),
          createdAt: getCurrentTimeCreate(),
        }),
      );
      dispatch(setReplied(true));
    } else if (chat.id && !user.emailAsked && !emailValidation(user.email)) {
      dispatch(
        addMessage({
          type: MESSAGE_TYPES.MESSAGE,
          uuid: uuidV4(),
          message: $l('ASK_EMAIL_TEXT'),
          createdAt: getCurrentTimeCreate(),
        }),
      );
      dispatch(addMessage({ type: MESSAGE_TYPES.ASK_EMAIL, uuid: uuidV4(), scroll: true }));
      dispatch(updateUser({ emailAsked: true }));
    }
  };

export const sendAttachment =
  (data: HTMLInputElement): TThunk =>
  async (dispatch, getState, api) => {
    const uuid = uuidV4();
    const { user, chat } = getState();

    if (!data.files?.length || !chat.id) return;

    const formData = new FormData();
    formData.append('chat_id', chat.id.toString());
    formData.append('hash', chat.hash);
    formData.append('uuid', uuid);
    Array.from(data.files).forEach((file: (typeof data.files)[0]) => {
      formData.append(file.name, file);
    });

    const attachments = Object.values(data.files).map((el) => ({ name: el.name, size: el.size }));
    try {
      dispatch(
        addMessage({
          user_id: user.id,
          chat_id: chat.id,
          hash: chat.hash,
          attachments,
          createdAt: getCurrentTimeCreate(),
          userType: MESSAGE_USER_TYPES.INTERNAL_USER,
          authorType: MESSAGE_USER_TYPES.INTERNAL_USER,
          type: MESSAGE_TYPES.ATTACHMENT,
          uuid,
          tempMessage: true,
        }),
      );
      const onUploading = (progress: number) => {
        dispatch(
          addMessage({
            uuid,
            loading: progress !== 100 ? progress : 0,
          }),
        );
      };
      await api.messages.sendAttachment(formData, onUploading);
    } catch (e) {
      dispatch(
        addMessage({
          formDataResend: formData,
          uuid,
          loading: 0,
          createdAt: null,
          error: true,
        }),
      );
    }
  };

export const readMessage =
  (messageId?: number): TThunk =>
  async (dispatch, getState, api) => {
    const chatLastMessage = getCookie(CHAT_LAST_MESSAGE);
    const lastBefore = chatLastMessage ? +chatLastMessage : 0;
    const chatLastMessageRead = getCookie(CHAT_LAST_MESSAGE_READ);
    const lastReadBefore = chatLastMessageRead ? +chatLastMessageRead : 0;

    const {
      messages: { messages },
      chat: { isOpen, isOnline, id, hash },
    } = getState();
    if (!isOnline || document.visibilityState === 'hidden' || !isOpen || !id) return;

    const messagesOfChat = messages.filter((message) => message.chatId === id);
    const lastMessageFromStaff = getLastMessage(messagesOfChat, true);

    const lastMessageId = messageId || lastMessageFromStaff;

    if (lastBefore > lastMessageId) return;

    setCookie(CHAT_LAST_MESSAGE, lastMessageId);

    if (lastMessageId === lastMessageFromStaff) {
      dispatch(setUnreadMessages(false));
    }

    if (lastReadBefore < lastMessageId) {
      try {
        await api.messages.readMessages({ chat_id: id, hash, message_id: lastMessageId });
        setCookie(CHAT_LAST_MESSAGE_READ, lastMessageId);
      } catch (e) {
        // empty
      }
    }
  };

export const checkLastMessage = (): TThunk => async (dispatch, getState, api) => {
  const {
    chat: { hash, id, isOnline },
    messages: { messages },
  } = getState();
  if (!isOnline || !id) return;

  try {
    const messagesOfChat = messages.filter((message) => message.chatId === id);
    const lastMessageId = getLastMessage(messagesOfChat);
    if (!lastMessageId) return;

    const {
      latest,
      messages: newMessages,
      status: chatStatus,
    } = await api.messages.checkLastMessage({ chat_id: id, hash, message_id: lastMessageId });

    if (latest && chatStatus !== CHAT_STATUS_CLOSED) return;

    if (newMessages.length) {
      dispatch(clearTempMessages());
    }

    let hasUnreadWithStaff = false;
    newMessages.forEach((message) => {
      const authorType = (message.authorType || message.authorType).toLowerCase();

      if (!MESSAGE_USER_TYPES_LIST.includes(authorType)) {
        dispatch(
          addSupport({
            id: message.authorableId,
            name: message.authorName,
            img: message.img,
          }),
        );

        hasUnreadWithStaff = true;
      }

      dispatch(addMessage(message));
    });
    if (chatStatus === CHAT_STATUS_CLOSED) {
      dispatch(closeChat());
    }

    const {
      chat: { isOpen },
    } = getState();

    if (isOpen && !window.document.hidden) {
      dispatch(readMessage());
    } else if (hasUnreadWithStaff) {
      dispatch(setUnreadMessages(true));
      playSound();
    }
  } catch (e) {
    /* empty */
  }
};

export const errorResend =
  (message: IMessage): TThunk =>
  async (dispatch, getState, api) => {
    if (message.type === MESSAGE_TYPES.MESSAGE) {
      dispatch(sendMessage(message.message, message.uuid));
    } else if (message.type === MESSAGE_TYPES.ATTACHMENT && message.formDataResend) {
      try {
        dispatch(
          addMessage({
            createdAt: getCurrentTimeCreate(),
            uuid: message.uuid,
            error: false,
          }),
        );
        const onUploading = (progress: number) => {
          dispatch(
            addMessage({
              uuid: message.uuid,
              loading: progress !== 100 ? progress : 0,
            }),
          );
        };
        await api.messages.sendAttachment(message.formDataResend, onUploading);

        dispatch(
          addMessage({
            createdAt: getCurrentTimeCreate(),
            formDataResend: null,
            uuid: message.uuid,
            error: false,
          }),
        );
      } catch (e) {
        dispatch(
          addMessage({
            formDataResend: message.formDataResend,
            uuid: message.uuid,
            loading: 0,
            createdAt: null,
            error: true,
          }),
        );
      }
    }
  };

export const answerCallback =
  (callbackId: number, messageId: number, answer: number): TThunk<Promise<void>> =>
  async (dispatch, getState, api) => {
    const {
      chat: { hash, id: chatId },
    } = getState();

    if (!chatId) return;

    await api.messages.updateCallback(callbackId, { hash, answer, chat_id: chatId });

    dispatch(updateCallbackAnswer({ messageId, answer }));
  };
